import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import i18n, { setVocabulary } from '@/admin/lib/i18n'
import type { Vocabulary } from '@/shared/lib/vocabulary'
import api from '@/admin/lib/api'

interface AvailableOrg {
  uuid: string
  name: string
  tenant_name: string | null
  is_active: boolean
}

interface TenantSettings {
  require_partner_on_registration?: boolean
  has_downstream_partnerships?: boolean
  /** Default Product Library view: "list" | "grid" | "categories". */
  products_library_default_view?: string
  /** Record-title typography: "editorial" (Cormorant serif) | "operational" (plain sans). */
  record_title_variant?: string
  /** Themable claim-detail card labels (defaults preserve today's wording). */
  claim_customer_card_label?: string
  claim_consumer_card_label?: string
  claim_evidence_card_label?: string
  /** Label for the organisation row on the claim Product card (e.g. "Supplier"). */
  claim_dealer_row_label?: string
  /**
   * The tenant's word for its partner network, derived from its external role
   * (Contractor / Dealer / Retailer / ..., default "Partner"/"Partners"). Used
   * to label partner-network surfaces (e.g. the dashboard) in the tenant's own
   * vocabulary. Resolved server-side; see apps.dashboards.vocabulary.
   */
  partner_noun?: string
  partner_noun_plural?: string
}

export interface PartnershipContext {
  upstream_organisation: {
    uuid: string
    name: string
    tenant_name: string | null
    tenant_slug: string | null
    logo_url: string | null
  }
  downstream_organisation: {
    uuid: string
    name: string
  }
  role_name: string | null
}

export interface OrgPreferences {
  default_currency: string
  available_currencies: string[]
  default_country_code: string
  default_language: string
}

type BrandingMode = 'GENERIC' | 'COBRANDED' | 'BRANDED' | 'DISABLED'

interface OrgContext {
  user: {
    uuid: string
    email: string
    first_name: string
    last_name: string
  }
  organisation: {
    uuid: string
    name: string
    logo_url: string | null
    tenant_slug: string | null
  } | null
  available_organisations?: AvailableOrg[]
  partnerships?: PartnershipContext[]
  is_partner_access?: boolean
  active_downstream_organisation_uuid?: string | null
  active_partnership_uuid?: string | null
  /** Active partner's display name (partner sessions only). */
  active_partner_name?: string | null
  /**
   * Sub-locations beneath the active partner. 0 = single site, nothing to
   * choose between, so partner-facing partner pickers stay hidden.
   */
  active_partner_location_count?: number
  permissions: string[]
  tenant_settings?: TenantSettings
  tenant_feature_flags?: Record<string, boolean>
  /** Sparse design-token overrides for this tenant (see shared/lib/theme). */
  tenant_theme?: Record<string, string>
  /** Tenant brand colour; seeds the brand token when not overridden in theme. */
  tenant_primary_colour?: string | null
  preferences?: OrgPreferences | null
  admin_branding?: BrandingMode
  /**
   * The tenant's own word for each domain object, as i18next variables
   * ({ warrantyNoun: 'Guarantee', ... }). Catalogue copy carries the slot;
   * this fills it, so a renamed tenant needs no translation overrides.
   */
  vocabulary?: Vocabulary
  /** Changes whenever any noun changes; see apps.core.vocabulary. */
  vocabulary_version?: string
}

export function useOrg() {
  const queryClient = useQueryClient()
  const query = useQuery<OrgContext>({
    queryKey: ['org-context'],
    queryFn: () => api.get('/business/me/').then((r) => r.data),
    staleTime: 60_000,
  })

  // Persist tenant slug so logout always redirects to the branded login
  const tenantSlug = query.data?.organisation?.tenant_slug
  useEffect(() => {
    if (tenantSlug) {
      sessionStorage.setItem('tenantSlug', tenantSlug)
    }
  }, [tenantSlug])

  // Sync i18n language with org preferences
  const preferredLanguage = query.data?.preferences?.default_language
  useEffect(() => {
    if (preferredLanguage && preferredLanguage !== i18n.language) {
      i18n.changeLanguage(preferredLanguage)
    }
  }, [preferredLanguage])

  // Fill the noun slots in catalogue copy with the tenant's own words. Keyed
  // on the version rather than the object so a refetch that changed nothing
  // does not re-render every translated string.
  const vocabulary = query.data?.vocabulary
  const vocabularyVersion = query.data?.vocabulary_version
  useEffect(() => {
    setVocabulary(vocabulary)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vocabularyVersion])

  const switchToPartner = useCallback(
    (upstreamOrgUuid: string, downstreamOrgUuid: string) => {
      sessionStorage.setItem('activeOrganisationUuid', upstreamOrgUuid)
      sessionStorage.setItem('activePartnerUuid', downstreamOrgUuid)
      queryClient.invalidateQueries({ queryKey: ['org-context'] })
    },
    [queryClient]
  )

  const leavePartnerContext = useCallback(() => {
    sessionStorage.removeItem('activeOrganisationUuid')
    sessionStorage.removeItem('activePartnerUuid')
    queryClient.invalidateQueries({ queryKey: ['org-context'] })
  }, [queryClient])

  // Partner context lives in sessionStorage, so it is gone on every fresh tab.
  // Only the dashboard offers the launchpad that restores it, so a partner user
  // who lands anywhere else (bookmark, emailed deep link, restored tab) had no
  // context in the request headers. A partner with exactly ONE partnership has
  // nothing to choose, so the backend auto-selects it server-side on the first
  // request (see ``Audience._sole_partnership_pair``): the very first
  // ``/business/me/`` already returns ``is_partner_access``, with no
  // set-header -> invalidate -> refetch round trip to race the deep link. Two or
  // more partnerships still get the launchpad card to pick from.
  const partnerships = query.data?.partnerships
  const isPartnerAccess = query.data?.is_partner_access ?? false
  const partnerOrgUuid = query.data?.organisation?.uuid ?? null
  const activeDownstreamUuid =
    query.data?.active_downstream_organisation_uuid ?? null
  useEffect(() => {
    // Backend already resolved partner access for us (sole-partnership
    // auto-select). Persist that pair so subsequent requests carry the
    // active-org headers explicitly and the server skips the re-resolution -
    // no refetch, the context is already correct.
    if (isPartnerAccess) {
      if (
        partnerOrgUuid &&
        activeDownstreamUuid &&
        sessionStorage.getItem('activePartnerUuid') === null
      ) {
        sessionStorage.setItem('activeOrganisationUuid', partnerOrgUuid)
        sessionStorage.setItem('activePartnerUuid', activeDownstreamUuid)
      }
      return
    }
    // Fallback for a backend that did not auto-resolve: set the sole
    // partnership's headers and refetch so partner context is established. A
    // no-op on the common path, where the server resolves it on the first
    // fetch and ``isPartnerAccess`` is already true above.
    if (!partnerships || partnerships.length !== 1) return
    if (sessionStorage.getItem('activePartnerUuid') !== null) return
    const only = partnerships[0]
    const upstream = only?.upstream_organisation?.uuid
    const downstream = only?.downstream_organisation?.uuid
    if (!upstream || !downstream) return
    sessionStorage.setItem('activeOrganisationUuid', upstream)
    sessionStorage.setItem('activePartnerUuid', downstream)
    queryClient.invalidateQueries({ queryKey: ['org-context'] })
  }, [
    partnerships,
    isPartnerAccess,
    partnerOrgUuid,
    activeDownstreamUuid,
    queryClient,
  ])

  return {
    ...query,
    orgUuid: query.data?.organisation?.uuid ?? null,
    orgName: query.data?.organisation?.name ?? null,
    orgLogoUrl: query.data?.organisation?.logo_url ?? null,
    adminBranding: (query.data?.admin_branding ?? 'GENERIC') as BrandingMode,
    availableOrgs: query.data?.available_organisations ?? [],
    partnerships: query.data?.partnerships ?? [],
    isPartnerAccess: query.data?.is_partner_access ?? false,
    activePartnershipUuid: query.data?.active_partnership_uuid ?? null,
    activePartnerName: query.data?.active_partner_name ?? null,
    activePartnerLocationCount: query.data?.active_partner_location_count ?? 0,
    tenantSettings: query.data?.tenant_settings ?? {},
    tenantTheme: query.data?.tenant_theme ?? {},
    tenantPrimaryColour: query.data?.tenant_primary_colour ?? null,
    preferences: query.data?.preferences ?? null,
    switchToPartner,
    leavePartnerContext,
  }
}
